import { ComponentBase } from "./ComponentBase.js";
import { template } from '../utils/TemplateParser';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { DateTime } from 'luxon';
import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timegridPlugin from '@fullcalendar/timegrid'; 
import luxonPlugin from '@fullcalendar/luxon3';
import { toLuxonDateTime } from '@fullcalendar/luxon3';
import { lang } from '../lang';
import { vars } from "../utils/vars.js";
import { Emitters } from '../utils/Emitters';

export class EventsGrid extends ComponentBase {
    constructor({listView}) {
        super();
        this.loading = new LoadingIndicator();
        this.listView = listView;
        
        // Wait for elements to be added before initializing calendar
        const observer = new MutationObserver((mutations) => {
            // Full Calendar renders strangely if everything isn't loaded, so give it a little extra time
            setTimeout(() => {
                this.initCalendar();
            }, 600);
            observer.disconnect();
        });
        observer.observe(this.el, {
            childList: true
        });

        this.render();
    }

    registerEvents() {
        this.addEventListener(vars.viewContainer, 'datepickerSelect', (e) => {
            this.fullCalendar?.gotoDate(e.detail.date);
        });
        this.addEventListener(vars.viewContainer, 'datepickerClear', (e) => {
            this.fullCalendar?.today();
        });
        return {
            'fetchComplete' : 'handleFetchComplete',
            'click .fc-next-button' : 'prevNextButton',
            'click .fc-prev-button' : 'prevNextButton',
            'click .fc-dayGridMonth-button, .fc-timeGridWeek-button, .fc-timeGridDay-button, .fc-col-header-cell-cushion[data-navlink]' : 'convertIcons',
            'click a[data-action="routeChange"]': 'handleRouteChange',
        }
    }

    /**
     * Load all events for the current month after clicking next/prev buttons
     */
    prevNextButton() {
        const date = toLuxonDateTime(this.fullCalendar.getDate(), this.fullCalendar);
        this.listView.currentDateRange.start = date.startOf('month').toISODate();
        this.listView.currentDateRange.end = date.endOf('month').toISODate();
        this.listView.refetch();
    }

    /**
     * Event List view can fetch events over the course of 2 months and miss parts
     * of the current month. Load the entire month
     */
    loadMonth() {
        const date = DateTime.fromISO(this.listView.currentDateRange.start).startOf('month');
        this.listView.currentDateRange.start = date.toISODate();
        this.listView.currentDateRange.end = date.endOf('month').toISODate();
        this.listView.refetch();
    }
    
    handleFetchComplete(e) {
        const events = e.detail;
        if (!this.fullCalendar) {
            // Save events to be displayed when calendar inits
            this.collection = events.map(event => {
                return this.formatEvent(event);
            });
        } else {
            this.fullCalendar.removeAllEvents();
            // Calendar has already initialized, add events
            events.forEach(event => {
                this.fullCalendar.addEvent(this.formatEvent(event));
            });
        }
    }

    template() {
        return template`
            <div id="calendar-month-view">
                ${this.loading}
            </div>
        `;
    }

    formatEvent(event) {
        event.url = this.getRouteURL(`event-details/${event.id}`);
        if(event.recurring) event.url += `/instances/${event.startDate ?? event.startDatetime}`;

        if (event.status == 'CANCELLED') {
            event.cancelled = true;
        }

        // Format Dates
        if (event.startDatetime) {
            event.start = formatDate(event.startDatetime);
            event.end = formatDate(event.endDatetime);
        } else {
            // All day events don't shift for timezones
            // Full Calendar's end date is exclusive for all day events, so add an exra day
            event.start = event.startDate;
            event.end = DateTime.fromISO(event.endDate, {zone: 'UTC'}).plus({ days: 1 }).toISODate();
            event.allDay = true;
        }
        return event;

        function formatDate(date) {
            return DateTime.fromISO(date, {zone: 'UTC'}).setZone(vars.pubcalendar.timezone).toISO();
        }
    }

    initCalendar() {
        const calendarEl = this.el.querySelector('#calendar-month-view');
        const calendarSettings = {
            plugins: [ luxonPlugin, dayGridPlugin, timegridPlugin ],
            timeZone: vars.pubcalendar.timezone,
            initialView: 'dayGridMonth',
            dayHeaderFormat: {
                weekday: 'long'
            },
            eventTimeFormat: { // example: 6:30pm or 5pm when zero minutes
                hour: 'numeric',
                minute: '2-digit',
                omitZeroMinute: true,
                meridiem: 'short'
            },
            headerToolbar: {
                center: "",
                right: "dayGridMonth,timeGridWeek,timeGridDay prev,next"
            },
            navLinks: true,		// makes day numbers clickable
            dayMaxEvents: 8,
            rerenderDelay: 1,
            viewDidMount: (args) => {
                // this method called after calendar added to DOM
                // make calendar accessible
                this.makeAccessible(args);
                Emitters.dispatchViewLoaded();
            },
            eventClassNames: (args) => {
                // this method called for every event to add class names
                // add support for cancelled events
                let classes = [];
                if (args.event?.extendedProps.cancelled){
                    classes.push('fc-event-cancelled');
                } else if (args.event?.extendedProps.status == 'POSTPONED'){
                    classes.push('fc-event-postponed');
                }
                return classes;
            },
            eventContent: (args) => {
                // this method called for every event 
                // add support for cancelled events
                if (args.event?.extendedProps.cancelled){
                    return {
                        html: `
                        <div class="fc-event-main-frame" title="${lang.EventCancelledTitle}">
                            <div class="fc-daygrid-cancelled-icon fas fa-times" aria-label="${lang.EventCancelledColon}"></div>
                            <div class="fc-event-title"> ${args.event.title}</div>
                        </div>`
                    }
                } else if (args.event?.extendedProps.status == 'POSTPONED'){
                    return {
                        html: `
                        <div class="fc-event-main-frame" title="${lang.EventPostponedTitle}">
                            <div class="fc-daygrid-cancelled-icon fas fa-clock" aria-label="${lang.EventPostponedColon}"></div>
                            <div class="fc-event-title"> ${args.event.title}</div>
                        </div>`
                    }
                }
                return true;
            },
            eventDidMount: (args) => {
                // Make events accessible via aria-description and aria-describedby
                if (args.event.end?.toDateString() === args.event.start.toDateString() || args.event.end == null){ // event only on single day
                    if (args.view.type == "dayGridMonth"){
                        let id = args.el.closest('[data-date]').getAttribute('data-date');
                        args.el.setAttribute('aria-describedby', id);
                        args.el.setAttribute('title', `${this.getTime(args.event.start)} - ${args.event.title}`);
                    }
                    else {
                        let description = `${args.event.title}: ${this.getMonthName(args.event.start)} ${args.event.start.getDate()}`;
                        if (args.event.start.getHours() !== 0){
                            description += ` at ${this.getTime(args.event.start)}`;
                        }
                        args.el.setAttribute('aria-label', description);
                        args.el.setAttribute('title', description);
                    }
                } else {
                    // multi-day event
                    let start = this.getMonthName(args.event.start) + ` ${args.event.start.getDate()}`;
                    let end = DateTime.fromJSDate(args.event.end);
                    const format = 'MMMM d';
                    if (args.event.end?.getHours() === 0){
                        // no end time. "End" prop (without time) is exclusive, so back up a day
                        end = end.minus({days: 1}).toFormat(format);
                    } else {
                        end = end.toFormat(format);
                    }

                    let description = `${args.event.title}: Runs from ${start} to ${end}`;
                    args.el.setAttribute('aria-label', description);
                    args.el.setAttribute('title', description);
                }
                args.el.setAttribute('data-action', 'routeChange');
            },
            loading: (isLoading) => {
                if(!isLoading) {
                    this.convertIcons();
                }
            },
        };

        // Add cached events to calendar
        if (this.collection) {
            calendarSettings.eventSources = [
                {
                    events: this.collection
                }
            ];
        }

        this.fullCalendar = new Calendar(calendarEl, calendarSettings);
        this.loading.destroy();
        this.fullCalendar.render();
    }

    convertIcons() {
        setTimeout(() => Emitters.dispatchConvertIcons(this.el), 1);
    }

    destroy() {
        this.fullCalendar?.destroy();
        super.destroy();
    }

    /*******
     HELPER METHODS
    ********/  

    setAttributes(el, attrs) {
        for(var key in attrs) {
            el.setAttribute(key, attrs[key]);
        }
    }

    // get month name. Accepts Date obj
    getMonthName(date){
        return new Intl.DateTimeFormat('en-US', {month: "long"}).format(date);
    }

    getTime(date){
        return DateTime.fromJSDate(date, {zone: vars.pubcalendar.timezone}).toFormat('h:mma').toLowerCase();
    }

    /*******
     ACCESSIBILITY METHODS
    ********/  

    makeAccessible(args){
        this.makeAccessiblePrevNextButtons(args);
        this.makeAccessibleMonthWeekDayButtons(args);
        this.makeAccessibleTitle(args);
        this.makeAccessibleDates(args);
        this.makeAccessibleEvents(args);

        // handle changing months
        let delay = 200;
        this.el.querySelector('.fc-prev-button').onclick = (e) => {
            setTimeout(this.makeAccessible(args), delay); // wait for new month to render
        }
        this.el.querySelector('.fc-next-button').onclick = (e) => {
            setTimeout(this.makeAccessible(args), delay); // wait for new month to render
        }
    }

    // next/previous buttons
    makeAccessiblePrevNextButtons(args, label = 'month'){
        this.el.querySelector('.fc-prev-button').setAttribute('aria-label', `${lang.Previous} ${label}`);
        this.el.querySelector('.fc-next-button').setAttribute('aria-label', `${lang.Next} ${label}`);
    }

    // month/week/day buttons
    makeAccessibleMonthWeekDayButtons(args){
        let mainViewId = 'fc-view-harness';

        this.setAttributes(
            this.el.querySelector('.fc-dayGridMonth-button'),
            {
                'aria-label': lang.MonthView,
                'role': 'tab',
                'aria-controls': mainViewId
            }
        );
        this.setAttributes(
            this.el.querySelector('.fc-timeGridWeek-button'),
            {
                'aria-label': lang.WeekView,
                'role': 'tab',
                'aria-controls': mainViewId
            }
        );
        this.setAttributes(
            this.el.querySelector('.fc-timeGridDay-button'),
            {
                'aria-label': lang.DayView,
                'role': 'tab',
                'aria-controls': mainViewId
            }
        );

        let buttonWrapper = this.el.querySelector('.fc-dayGridMonth-button').parentElement;
        this.setAttributes(
            buttonWrapper,
            {
                'aria-label': lang.SelectView,
                'role': 'tablist'
            }
        );

        // add aria-selected on page load
        for (let el of buttonWrapper.children) {
            if (el.classList.contains('fc-button-active')){
                // set as selected
                el.setAttribute("aria-selected", true);
            } else {
                // set as not selected
                el.setAttribute("aria-selected", false);
            }
        };
        // update aria-selected on click
        buttonWrapper.onclick = (e) => {
            if (e.target.classList.contains("fc-button")){
                for (let el of e.target.parentElement.children) {
                    el.setAttribute("aria-selected", false);
                };

                e.target.setAttribute("aria-selected", true);
                this.makeAccessiblePrevNextButtons(args, e.target.innerHTML);
            }
        };

        // add id to wrapper
        this.el.querySelector(".fc-view-harness").id = mainViewId;
    }

    // aria live to date title
    makeAccessibleTitle(args){ 
        this.el.querySelector(".fc-toolbar-title").setAttribute('aria-live', 'polite');
    }

    // add aria-label to date numbers (dayGridMonth)
    makeAccessibleDates(args){
        let d = new Date(args.view.currentStart);
        let currentMonthName = this.getMonthName(d);
        let currentDateElements = args.el.querySelectorAll('.fc-daygrid-day:not(.fc-day-other) .fc-daygrid-day-number');

        // previous month dates
        d = new Date(args.view.activeStart);
        let pastMonthName = this.getMonthName(d);
        let pastMonthDateElements = args.el.querySelectorAll('.fc-daygrid-body table tbody tr:first-child .fc-day-other .fc-daygrid-day-number, .fc-daygrid-body table tbody tr:nth-child(2) .fc-day-other .fc-daygrid-day-number');

        // next month dates
        d = new Date(args.view.activeEnd);
        let futureMonthName = this.getMonthName(d);
        let futureMonthDateElements = args.el.querySelectorAll('.fc-daygrid-body table tbody tr:last-child .fc-day-other .fc-daygrid-day-number, .fc-daygrid-body table tbody tr:nth-last-child(2) .fc-day-other .fc-daygrid-day-number');

        for (let date of currentDateElements) {
            let label = currentMonthName + " " + date.innerHTML;
            if (date.closest('.fc-day-today') !== null){
                label = lang.TodayColon + label;
            }
            date.setAttribute('aria-label', label);
        }
        for (let date of pastMonthDateElements) {
            date.setAttribute('aria-label', pastMonthName + " " + date.innerHTML);
        }
        for (let date of futureMonthDateElements) {
            date.setAttribute('aria-label', futureMonthName + " " + date.innerHTML);
        }
    }

    // add id to date numbers for events to reference via aria-describedby (dayGridMonth)
    makeAccessibleEvents(args) {
        let days = args.el.querySelectorAll('.fc-daygrid-day-number');

        // add ids to days
        for (let day of days){
            let fullDate = day.closest("[data-date]").getAttribute('data-date');
            day.id = fullDate;
        }
    }

}